import { inject, injectable } from "@launchtray/tsyringe-async";
import { AsyncOperationNotification } from "by/gamefactory/cityversetycoon/scenes/common/components/AsyncOperationNotification";
import { PaymentResult } from "by/gamefactory/web3/PaymentResult";
import { IUseDiscountController } from "./IUseDiscountController";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { CancellationToken } from "async/CancellationToken";
import { CancellationTokenSource } from "async/CancellationTokenSource";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { EthL1PaymentProcessingModule } from "../../../ethL1paymentprocessing/EthL1PaymentProcessingModule";
import { PaymentProcessingType } from "../../../ethL1paymentprocessing/type/PaymentProcessingType";
import { EthL1PaymentProcessingPopupSceneOptions } from "../../../ethL1paymentprocessing/scenes/EthL1PaymentProcessingPopupSceneOptions";
import { Erc20PaymentProcessingModule } from "../../../ethL1paymentprocessing/Erc20PaymentProcessingModule";
import { PaymentProcessingPopupSceneOptions } from "../../../ethL1paymentprocessing/scenes/PaymentProcessingPopupSceneOptions";
import { Web3Config } from "by/gamefactory/web3/config/Web3Config";

@injectable()
export class UseDiscountController implements IUseDiscountController {
    private readonly contextResolver: IContextResolver;
    private readonly web3Model: Web3Model;
    private readonly web3Config: Web3Config;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    public constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        web3Model: Web3Model,
        web3Config: Web3Config,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        this.contextResolver = contextResolver;
        this.web3Model = web3Model;
        this.web3Config = web3Config;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
    }

    public async showSelectPaymentMethodPopup(
        buildingId: string,
        cancellationToken: CancellationToken,
        price: number,
        couponBenefitIds?: Array<string>
    ): Promise<PaymentResult> {
        const cancellationTokenSource = new CancellationTokenSource(cancellationToken);

        try {
            this.asyncOperationNotification.show();
            const cryptoPaymentProcessingModule = await this.contextResolver.resolve(Erc20PaymentProcessingModule);
            const tokenSymbol = await this.web3Model.getTokenSymbol(this.web3Config.targetChain, this.web3Config.reservoirDevUsdcCurrency);
            this.asyncOperationNotification.hide();
            return await cryptoPaymentProcessingModule.start({
                chain: this.web3Config.targetChain,
                erc20Address: this.web3Config.reservoirDevUsdcCurrency,
                tokenSymbol,
                type: PaymentProcessingType.BUILDING,
                price,
                buildingId,
                couponBenefitIds,
            } as PaymentProcessingPopupSceneOptions<PaymentProcessingType.STORE_PACKAGE>);
        } catch (e: unknown) {
            this.logger.error("Exception on process payment building with mint: ", e);
            return PaymentResult.FAILURE;
        } finally {
            cancellationTokenSource.dispose();
        }
    }

    public async showMonPaymentProcessingPopup(buildingId: string, price: number, couponBenefitIds?: Array<string>): Promise<PaymentResult> {
        const monPaymentProcessingModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(EthL1PaymentProcessingModule));
        return await monPaymentProcessingModule.start({
            type: PaymentProcessingType.BUILDING,
            price,
            buildingId,
            couponBenefitIds,
        } as EthL1PaymentProcessingPopupSceneOptions<PaymentProcessingType.BUILDING>);
    }
}
